import { styled } from '@mui/material'
import L, { LatLngExpression } from 'leaflet'
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Circle, LayerGroup, Polyline, Popup, Tooltip, useMap, useMapEvent } from 'react-leaflet'
import { getPositionName } from 'src/utils/function'
import { LocalityCageStatusCard } from '../../../../../components/locality-dashboard/CageStatusMapCard'
import { IMainComponentType } from '../../../../../enums'
import { IMooringType, IPosition } from '../../../../../generated-types'
import { grayedOpacity, letterArray, mapOptions, minZoom, opacity } from '../constraints'
import { getContrastTextColor, getDistance, midpoint, triangleCentroid } from '../utils'
import { useShowHiddenPositions } from '../ShowHiddenPositionsContext'
import { useCageLabelFontSize } from '../CageLabelFontSizeContext'
import { CageLabel } from './CageLabel'
import { useMooringsCtx } from 'src/hooks/context-hooks'

interface ICage {
  position: IPosition
  navigateToPosition: (positionId: number) => void
  steelFrame?: boolean
  showCageStatusPopOver?: boolean
  mooringType?: IMooringType
  showCriticalPositions?: boolean
  isSmallMapView?: boolean
}

export const StyledPopup = styled(Popup)({
  width: '275px',
  '& .leaflet-popup-content': { margin: '0px' },
  '& .leaflet-popup-content p': { margin: '0px' },
  '& .leaflet-popup-content-wrapper': { padding: '0px', boxShadow: '0px 3px 6px #00000040' },
})

export const Cage: React.FC<ICage> = memo(
  ({
    position,
    navigateToPosition,
    steelFrame = false,
    showCageStatusPopOver,
    mooringType,
    showCriticalPositions = false,
    isSmallMapView = false,
  }) => {
    const showHiddenPositions = useShowHiddenPositions()
    const sharedFontSize = useCageLabelFontSize()
    const map = useMap()
    const [zoomLevel, setZoomLevel] = useState(Math.floor(map.getZoom()))
    const cagePopupRef = React.useRef(null)
    const { t: translate } = useTranslation(['common'])
    const { isCritical } = useMooringsCtx()
    const shouldHighlightCritical = !!(isCritical || showCriticalPositions)

    useEffect(() => {
      setZoomLevel(Math.floor(map.getZoom()))
    }, [map.getZoom()])

    const handleZoomEnd = event => {
      const newZoomLevel = event.target.getZoom()
      setZoomLevel(Math.floor(newZoomLevel))
    }

    const handleClose = useCallback(() => {
      const popup = cagePopupRef.current as any
      popup?.closePopup()
    }, [cagePopupRef])

    const handleOpen = useCallback(() => {
      const popup = cagePopupRef.current as any
      popup?.openPopup()
    }, [cagePopupRef])

    const netResources = useMemo(() => {
      return position.mainComponents?.filter(x => x.type.componentType === IMainComponentType.Net)
    }, [position.mainComponents])

    const ringResources = useMemo(() => {
      return position.mainComponents?.filter(x => x.type.componentType === IMainComponentType.Ring)
    }, [position.mainComponents])

    const netFilColor = useMemo(() => {
      return netResources && netResources.length > 0
        ? netResources?.some(x => x.activeDeviationsCount ?? 0 > 0)
          ? mapOptions.deviationColor
          : mapOptions.netColor
        : opacity !== 1.0
        ? mapOptions.netColor
        : mapOptions.netColorInactive
    }, [netResources, mapOptions, position.deviationsCount])

    const ringFilColor = useMemo(() => {
      return ringResources && ringResources.length > 0
        ? ringResources?.some(x => x.activeDeviationsCount ?? 0 > 0)
          ? mapOptions.deviationColor
          : mapOptions.ringColor
        : opacity !== 1.0
        ? mapOptions.ringColor
        : mapOptions.ringColorInactive
    }, [ringResources, mapOptions, position.deviationsCount])

    const netTextColor = useMemo(() => getContrastTextColor(netFilColor), [netFilColor])

    const fontSize = useMemo(() => {
      return sharedFontSize ?? (zoomLevel - minZoom <= 0 ? 8 : (zoomLevel - minZoom) * 8)
    }, [sharedFontSize, zoomLevel])

    // Keep the label short at low zoom (small circles), but reveal more of the name as we zoom in.
    // At minZoom the cap stays at 4 chars; each zoom level in adds room for a few more characters so
    // the full cage name (without the "...") becomes visible once there is space for it.
    const labelMaxLength = useMemo(() => {
      const zoomAboveMin = Math.max(0, zoomLevel - minZoom)
      return 1 + zoomAboveMin * 2
    }, [zoomLevel])

    useMapEvent('zoomend', handleZoomEnd)
    // Hidden positions are drawn greyed out when "show hidden" is on, so they can be found.
    if (position.isHidden && !showHiddenPositions) return <></>
    if (!position.connectedPositions[0]) return <></> //hide if visible set to false.
    if (position.isHidden) position = { ...position, isTransparent: true }
    const fillOpacity = position.isTransparent ? grayedOpacity : opacity

    let middle

    let netLatLongs: LatLngExpression = [
      position.connectedPositions[0].latitude,
      position.connectedPositions[0].longitude,
    ]
    let netPathOptions: L.PathOptions = {}
    const baseWeight = zoomLevel - minZoom <= 0 ? 1 : zoomLevel - minZoom
    netPathOptions.weight = position.isCritical && shouldHighlightCritical ? baseWeight + 1.5 : baseWeight
    let netToolTip = ''
    let netText = ''
    let netRadius = 0

    let ringLatLongs: LatLngExpression = [
      position.connectedPositions[0].latitude,
      position.connectedPositions[0].longitude,
    ]

    let ringPathOptions: L.PathOptions = {}
    let ringToolTip = ''
    let ringRadius = 0

    if (!steelFrame && position.connectedPositions[1]) {
      middle = midpoint(
        position.connectedPositions[0].latitude,
        position.connectedPositions[0].longitude,
        position.connectedPositions[1].latitude,
        position.connectedPositions[1].longitude,
        0.5,
      )

      if (
        position.connectedPositions[2] &&
        !isNaN(position.connectedPositions[2].latitude) &&
        isNaN(position.connectedPositions[2].latitude)
      ) {
        const triangleMidpoint = triangleCentroid([
          [position.connectedPositions[0].latitude, position.connectedPositions[0].longitude],
          [position.connectedPositions[1].latitude, position.connectedPositions[1].longitude],
          [position.connectedPositions[2].latitude, position.connectedPositions[2].longitude],
        ])

        middle = triangleMidpoint

        const distance = getDistance(
          triangleMidpoint[0],
          triangleMidpoint[1],
          position.connectedPositions[0].latitude,
          position.connectedPositions[0].longitude,
        )

        netRadius = (distance / 2) * 2

        netLatLongs = [triangleMidpoint[0], triangleMidpoint[1]]
        netToolTip = mapOptions.lang.net + ' ' + getPositionName(position)
        netText = position.name
        netPathOptions = {
          stroke: true,
          color: position.isCritical && shouldHighlightCritical ? netFilColor : 'gray',
          weight: position.isCritical && shouldHighlightCritical ? 4 : undefined,
          fillColor: netFilColor,
          fillOpacity,
          opacity: 100,
          pane: position.isWorkAssignmentPosition ? 'netUpdated' : 'net',
        }

        return (
          <LayerGroup key={position.id}>
            <Circle
              className="net"
              eventHandlers={{
                add: (e: any) => {
                  const el = (e.target as L.Circle).getElement()
                  if (el) (el as HTMLElement).setAttribute('data-test-id', 'map-cage-marker')
                },
                click: () => {
                  navigateToPosition(position.id)
                },
              }}
              center={netLatLongs as LatLngExpression}
              pane={netPathOptions.pane}
              pathOptions={{ fillOpacity: 0, opacity: 0 }}
              radius={netRadius}
            >
              <Tooltip permanent direction="center" offset={[0, 0]} className="permanent-tooltip">
                <CageLabel text={netText} color={netTextColor} fontSize={fontSize} isSmallMapView={isSmallMapView} />
              </Tooltip>
            </Circle>
            <Circle
              className="shadow-net"
              center={netLatLongs as LatLngExpression}
              pane={netPathOptions.pane}
              pathOptions={netPathOptions}
              radius={netRadius}
              eventHandlers={{
                add: (e: any) => {
                  const el = (e.target as L.Circle).getElement()
                  if (el) (el as HTMLElement).setAttribute('data-test-id', 'map-cage-shadow-net')
                },
                click: () => {
                  navigateToPosition(position.id)
                },
              }}
            >
              <Tooltip>
                <strong data-test-id="map-hover-tooltip">Net {netToolTip}</strong>
              </Tooltip>
            </Circle>
          </LayerGroup>
        )
      }
      const distance = getDistance(
        position.connectedPositions[0].latitude,
        position.connectedPositions[0].longitude,
        position.connectedPositions[1].latitude,
        position.connectedPositions[1].longitude,
      )

      ringRadius = ((distance / 100) * 40) / 2
      ringLatLongs = L.latLng(middle[0], middle[1])
      ringToolTip = mapOptions.lang.ring + ' ' + getPositionName(position)
      ringPathOptions = {
        stroke: !!(position.isCritical && shouldHighlightCritical),
        color: position.isCritical && shouldHighlightCritical ? ringFilColor : undefined,
        weight: position.isCritical && shouldHighlightCritical ? 4 : 0,
        fillColor: ringFilColor,
        fillOpacity,
        opacity: position.isCritical && shouldHighlightCritical ? 1 : 0,
        pane: position.isWorkAssignmentPosition ? 'ringUpdated' : 'ring',
      }

      netLatLongs = L.latLng(middle[0], middle[1])
      netToolTip = `${getPositionName(position)}`
      netText = position.name
      netRadius = ringRadius - 2
      netPathOptions = {
        stroke: false,
        fillColor: netFilColor,
        fillOpacity,
        opacity: 0,
        pane: position.isWorkAssignmentPosition ? 'netUpdated' : 'net',
      }

      return (
        <LayerGroup key={position.id}>
          <Circle
            className="cage"
            center={netLatLongs as LatLngExpression}
            pane={netPathOptions.pane}
            pathOptions={netPathOptions}
            radius={netRadius}
            eventHandlers={{
              add: (e: any) => {
                const el = (e.target as L.Circle).getElement()
                if (el) (el as HTMLElement).setAttribute('data-test-id', 'map-cage-marker')
              },
              click: () => {
                navigateToPosition(position.id)
              },
            }}
          ></Circle>
          <Circle
            className="ring"
            center={ringLatLongs as LatLngExpression}
            pane={ringPathOptions.pane}
            pathOptions={ringPathOptions}
            radius={ringRadius}
            eventHandlers={{
              add: (e: any) => {
                const el = (e.target as L.Circle).getElement()
                if (el) (el as HTMLElement).setAttribute('data-test-id', 'map-cage-ring')
              },
              click: () => {
                navigateToPosition(position.id)
              },
            }}
          >
            <Tooltip>
              <strong data-test-id="map-hover-tooltip">{ringToolTip}</strong>
            </Tooltip>
          </Circle>
          <Circle
            className="net"
            center={netLatLongs as LatLngExpression}
            pane={netPathOptions.pane}
            pathOptions={{ fillOpacity: 0, opacity: 0 }}
            radius={netRadius}
          >
            <Tooltip permanent direction="center" offset={[0, 0]} className="permanent-tooltip">
              <CageLabel
                text={getPositionName(position)}
                color={netTextColor}
                fontSize={fontSize}
                maxLength={labelMaxLength}
                isSmallMapView={isSmallMapView}
                bold
              />
            </Tooltip>
          </Circle>
          <Circle
            className="shadow-net"
            center={netLatLongs as LatLngExpression}
            pane={netPathOptions.pane}
            pathOptions={netPathOptions}
            radius={netRadius}
            ref={cagePopupRef}
            eventHandlers={{
              add: (e: any) => {
                const el = (e.target as L.Circle).getElement()
                if (el) (el as HTMLElement).setAttribute('data-test-id', 'map-cage-shadow-net')
              },
              click: _ => navigateToPosition(position.id),
              contextmenu: event => event.target.openPopup(),
              mouseout: event => event.target.closePopup(),
            }}
          >
            {showCageStatusPopOver ? (
              <StyledPopup closeButton={false} autoPan={false} autoClose={true}>
                <div data-test-id="map-popup-content">
                  <LocalityCageStatusCard
                    referenceName={`${getPositionName(position)} - ${translate(IMooringType[mooringType as number], {
                      ns: 'mooring',
                    })}`}
                    mainComponentStatus={position.mainComponentStatus}
                    handleClose={handleClose}
                    handleOpen={handleOpen}
                  />
                </div>
              </StyledPopup>
            ) : (
              <Tooltip>
                <strong data-test-id="map-hover-tooltip">Net {netToolTip}</strong>
              </Tooltip>
            )}
          </Circle>
        </LayerGroup>
      )
    }

    if (!position.connectedPositions[2]) return <></>

    middle = midpoint(
      position.connectedPositions[0].latitude,
      position.connectedPositions[0].longitude,
      position.connectedPositions[2].latitude,
      position.connectedPositions[2].longitude,
      0.5,
    )
    const latLongs = [
      [position.connectedPositions[0].latitude, position.connectedPositions[0].longitude],
      [position.connectedPositions[1].latitude, position.connectedPositions[1].longitude],
      [position.connectedPositions[2].latitude, position.connectedPositions[2].longitude],
      [position.connectedPositions[3].latitude, position.connectedPositions[3].longitude],
      [position.connectedPositions[0].latitude, position.connectedPositions[0].longitude],
    ]

    netToolTip = mapOptions.lang.cage + ' ' + getPositionName(position)
    netPathOptions = {
      // simplepoly: 1,
      fillOpacity,
      fillColor: netFilColor,
      weight: position.isCritical && shouldHighlightCritical ? 8 : 4,
      color: mapOptions.frameLineColor,
      pane: position.isWorkAssignmentPosition ? 'netUpdated' : 'net',
    }
    return (
      <LayerGroup key={position.id}>
        <Polyline
          key={position.id}
          positions={latLongs as LatLngExpression[]}
          pathOptions={netPathOptions}
          pane={netPathOptions.pane}
          eventHandlers={{
            add: (e: any) => {
              const el = (e.target as L.Polyline).getElement()
              if (el) (el as HTMLElement).setAttribute('data-test-id', 'map-cage-marker')
            },
            click: () => {
              navigateToPosition(position.id)
            },
          }}
        >
          <Tooltip sticky>
            <strong data-test-id="map-hover-tooltip">{getPositionName(position)}</strong>
          </Tooltip>
        </Polyline>
        <Circle
          className="net"
          center={[middle[0], middle[1]] as LatLngExpression}
          pane={netPathOptions.pane}
          pathOptions={{
            color: 'none',
            fillColor: 'none',
            fillOpacity: 0,
            opacity: 0,
            pane: position.isWorkAssignmentPosition ? 'cageUpdated' : 'cage',
            fill: true,
          }}
          radius={15 * 0.6}
        >
          <Tooltip permanent direction="center" offset={[0, 0]} className="permanent-tooltip">
            <CageLabel
              text={position?.reference ?? letterArray[parseInt(netText) - 901]}
              color={netTextColor}
              fontSize={fontSize}
              isSmallMapView={isSmallMapView}
            />
          </Tooltip>
        </Circle>
      </LayerGroup>
    )
  },
)
